import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
  PropsWithChildren,
} from 'react';
import { useRouter } from 'next/router';
import { createListingParams } from 'contexts/preferences/listing-params';
import { createSchoolParams } from 'contexts/preferences/school';
import storage from 'utils/storage';
import Cookies from 'js-cookie';
import { isTrustarcCookieTypeAllowed } from 'utils/trustarc-cookies';
import {
  DEFAULT_EXPIRE_DAYS,
  getLastSearchLocationTag,
  getRecentSearchesTag,
  getDetailedRecentSearchesTag,
} from 'constants/cookies';
import {
  ACTIVECAMPAIGN_SCRIPT,
  GTM_SCRIPT,
  NEWRELIC_SCRIPT,
} from 'constants/HTMLAttributes';
import { useUserContext } from '../user';
import { getUserLastSearch } from 'pages/api/last-search/lastSearchApiClient';
import { checkLocationHasAreaPageWithId, MILITARY_BASES_SEARCH_PREDICTION_GROUP } from 'data/search-predictions';
import { findLocationBoundaryByPlaceId } from 'utils/google-maps/geoLocator';
import { AREA_LISTINGS_ROUTE, getRouteNameFromPath } from 'components/dynamic-page/route-matchers';
import { updateSchoolParamsCookie } from './school';
import { cloneDeep } from 'lodash';
import { Prettify } from 'types/prettify';
import { getBoundingBoxCoordinates } from 'components/pages/area-listings/util/geo';
import { getTenantById, isTenantExpUS } from '@tenants';
import { addToTopWithDeduplication } from 'utils/array';
import { buildDetailedRecentSearchKey, buildFilterSnapshot, buildLocationFromFilter, upsertCurrentLocationFilter } from 'contexts/preferences/detailed-recent-searches';

import type SearchPrediction from 'data/search-predictions';
import type { ListingParams, ListingParamsMethods, Filter } from 'contexts/preferences/listing-params/types';
import type { DetailedRecentSearchEntry } from 'contexts/preferences/detailed-recent-searches';
import type { SchoolParams as TSchoolParams } from 'contexts/preferences/school/types';
import type SchoolParams from 'contexts/preferences/school';
import type { Screen } from 'utils/types';
import type { PropsWithTenantId } from '@tenants/types';

export interface LastSearchLocation extends SearchPrediction {
  boundary: string | null;
}

export type GlobalFilterPreferences = {
  listingParams: ListingParams & ListingParamsMethods; // FIXME: listingParams updates when saved.
};

export type IPreferences = Prettify<
{
  schoolParams: SchoolParams;
  updateSchoolParams: (params: Partial<SchoolParams>) => void;
  screen?: Screen;
  recentSearches: SearchPrediction[] | undefined;
  /** Only populated for tenants with the UseDetailedRecentSearches feature on. */
  detailedRecentSearches: DetailedRecentSearchEntry[] | undefined;
  lastSearchLocation?: LastSearchLocation;
  addToRecentSearches: (searchPrediction: SearchPrediction, filter?: Filter) => void;
  /** No-op for tenants without UseDetailedRecentSearches. See PreferencesContextProvider. */
  refreshCurrentLocationFilter: (filter: Filter) => void;
  areFunctionalCookiesEnabled: boolean;
  isMapSearchOpen: boolean;
  setIsMapSearchOpen: (isOpen: boolean) => void;
  isAiSearch: boolean;
  setIsAiSearch: (value: boolean) => void;
}
& GlobalFilterPreferences>;

type PreferencesContextProps = PropsWithTenantId<PropsWithChildren<{
  listingParamsData: ListingParams;
  schoolParamsData: TSchoolParams;
  screen?: Screen;
  functionalCookiesEnabled: boolean;
  advertisingCookiesEnabled: boolean;
  lastSearchLocationCookie?: LastSearchLocation;
  isAiSearchCookie: boolean;
}>>;

export const PreferencesContext = createContext<IPreferences | Record<string, unknown>>({});

export function usePreferencesContext <T extends IPreferences | Record<string, unknown> = IPreferences>() {
  return useContext(PreferencesContext) as T;
}

export default function PreferencesContextProvider({ listingParamsData, schoolParamsData, screen, functionalCookiesEnabled, advertisingCookiesEnabled, lastSearchLocationCookie, isAiSearchCookie, tenantId, children }: PreferencesContextProps) {
  const { user } = useUserContext();
  const tenant = getTenantById(tenantId);
  const localStorageTag = getRecentSearchesTag(tenant.theme.id);
  const detailedLocalStorageTag = getDetailedRecentSearchesTag(tenant.theme.id);
  const [listingParams, setListingParams] = useState(() => {
    if (user) {
      if (user.hideImagelessListings !== undefined && user.hideImagelessListings !== null) {
        const originalParams = cloneDeep(listingParamsData);
        originalParams.filter.hasImage = user.hideImagelessListings;
        return createListingParams(originalParams);
      }
    }
    return createListingParams(listingParamsData);
  });
  const [schoolParams, setSchoolParams] = useState(createSchoolParams(schoolParamsData));
  const [recentSearches, setRecentSearches] = useState(storage.get(localStorageTag));
  const [detailedRecentSearches, setDetailedRecentSearches] = useState(
    tenant.features.UseDetailedRecentSearches ? storage.get(detailedLocalStorageTag) : undefined
  );
  const [areFunctionalCookiesEnabled, setAreFunctionalCookiesEnabled] = useState(functionalCookiesEnabled);
  const [areAdvertisingCookiesEnabled, setAreAdvertisingCookiesEnabled] = useState(advertisingCookiesEnabled);
  const [lastSearchLocation, setLastSearchLocation] = useState<LastSearchLocation | undefined>(lastSearchLocationCookie);
  const [isAiSearch, setIsAiSearch] = useState(isAiSearchCookie);
  
  const router = useRouter();
  const isOnAreaPage = getRouteNameFromPath(router.asPath) === AREA_LISTINGS_ROUTE;
  const isOnSearchPage = router.asPath.startsWith('/search');
  const isExpUsTenant = isTenantExpUS(tenantId);
  const initialMapSearchOpen = router.query && !!router.query.openSearch && isExpUsTenant && isOnSearchPage;
  const [isMapSearchOpen, setIsMapSearchOpen] = useState(initialMapSearchOpen);

  const getUserLastSearchParams = useCallback(async () => {
    const hasUrlParams = !!window.location.search.length;
    const doesSearchHaveUrlParams = isOnSearchPage && hasUrlParams;
    if (user && !isOnAreaPage && !doesSearchHaveUrlParams) {
      const userLastSearchParams = await getUserLastSearch({ userId: user.id, sourceTheme: tenant.theme.id });
      if (userLastSearchParams && 'lastSearch' in userLastSearchParams) {
        if (user) {
          if (user.hideImagelessListings !== undefined && user.hideImagelessListings !== null) {
            userLastSearchParams.lastSearch.filter.hasImage = user.hideImagelessListings;
            setListingParams(createListingParams(userLastSearchParams.lastSearch));
          }
        }
      }
    }
  }, [isOnSearchPage, user, isOnAreaPage, tenant.theme.id, setListingParams]);

  const addToRecentSearches = useCallback(async (searchPrediction: SearchPrediction, filter?: Filter) => {
    // Saving new recent searches
    // This temporary to check we are no longer saving predictions with no location
    if (!searchPrediction.data?.position || searchPrediction.data?.position.coordinates[0] === 0 || searchPrediction.data?.position.coordinates[1] === 0) {
      console.warn('### No location found for search prediction', searchPrediction.description, searchPrediction.id);
    }

    // Use current state if available, otherwise fall back to localStorage
    // This ensures we're working with the most up-to-date data
    const storedSearches = storage.get(localStorageTag) as SearchPrediction[] | undefined;
    const currentRecentSearches: SearchPrediction[] | undefined = (recentSearches as SearchPrediction[] | undefined) || storedSearches;

    // Add new search to top, remove duplicates, and enforce max limit
    const newRecentSearches = addToTopWithDeduplication(
      currentRecentSearches,
      searchPrediction,
      search => search.description
    );
    setRecentSearches(newRecentSearches);
    storage.set(localStorageTag, newRecentSearches);

    // Separate, tenant-gated store carrying the active filter alongside the
    // location — additive only, never layered onto the plain list above, so
    // every existing consumer of recentSearches is untouched. Same city +
    // different filter is intentionally a distinct entry (see
    // buildDetailedRecentSearchKey), not a dedup/replace.
    if (filter && tenant.features.UseDetailedRecentSearches) {
      const filterSnapshot = buildFilterSnapshot(filter);
      const newEntry: DetailedRecentSearchEntry = {
        key: buildDetailedRecentSearchKey(searchPrediction.description, filterSnapshot),
        location: searchPrediction,
        filterSnapshot,
        searchedAt: new Date().toISOString(),
      };
      const storedDetailedSearches = storage.get(detailedLocalStorageTag) as DetailedRecentSearchEntry[] | undefined;
      const currentDetailedSearches = (detailedRecentSearches as DetailedRecentSearchEntry[] | undefined) || storedDetailedSearches;
      const newDetailedSearches = addToTopWithDeduplication(
        currentDetailedSearches,
        newEntry,
        entry => entry.key
      );
      setDetailedRecentSearches(newDetailedSearches);
      storage.set(detailedLocalStorageTag, newDetailedSearches);
    }

    // Saving new last search if it's a location or military base
    if (searchPrediction?.group === 'locations' || searchPrediction?.group === MILITARY_BASES_SEARCH_PREDICTION_GROUP) {
      let newLastSearch = searchPrediction;
      if (searchPrediction?.group === 'locations' && !checkLocationHasAreaPageWithId(searchPrediction.id)) {
        // If not an area page location, save boundary so this can be used on homepage components to fetch listings
        const boundary = await findLocationBoundaryByPlaceId(searchPrediction.id);
        newLastSearch = { ...searchPrediction, boundary } as LastSearchLocation;
      }
      // If a military base, save a boundary around the point since fetching home page listings by military slug seems to not work
      else if (searchPrediction?.group === MILITARY_BASES_SEARCH_PREDICTION_GROUP) {
        const boundaryCoordinates = getBoundingBoxCoordinates(searchPrediction.data?.position.coordinates[1], searchPrediction.data?.position.coordinates[0]);
        const boundary = {
          type: 'Polygon',
          coordinates: [[boundaryCoordinates.boundaryTopLeft, boundaryCoordinates.boundaryBottomRight]],
        };
        newLastSearch = { ...searchPrediction, boundary: JSON.stringify(boundary) } as LastSearchLocation;
      }
      setLastSearchLocation(newLastSearch as LastSearchLocation);
      Cookies.set(getLastSearchLocationTag(tenant.theme.id), JSON.stringify(newLastSearch), { expires: DEFAULT_EXPIRE_DAYS });
    }
  }, [localStorageTag, tenant.theme.id, detailedLocalStorageTag, tenant.features.UseDetailedRecentSearches]);

  // Companion to the detailed-write block in addToRecentSearches above, for
  // filter changes on an already-loaded area/hybrid page — those never go
  // through a SearchPrediction/transitionToPath, so nothing else captures
  // filter refinements made after the initial search that landed here.
  const refreshCurrentLocationFilter = useCallback((filter: Filter) => {
    if (!tenant.features.UseDetailedRecentSearches || !filter.areaName) return;

    const location = buildLocationFromFilter(filter);
    const storedDetailedSearches = storage.get(detailedLocalStorageTag) as DetailedRecentSearchEntry[] | undefined;
    const currentDetailedSearches = (detailedRecentSearches as DetailedRecentSearchEntry[] | undefined) || storedDetailedSearches;
    const newDetailedSearches = upsertCurrentLocationFilter(currentDetailedSearches, location, filter, new Date().toISOString());
    setDetailedRecentSearches(newDetailedSearches);
    storage.set(detailedLocalStorageTag, newDetailedSearches);
  }, [detailedLocalStorageTag, detailedRecentSearches, tenant.features.UseDetailedRecentSearches]);

  const handleNewMessages = useCallback((e: MessageEvent) => {
    try {
      let data = e.data;
      // only parse the data if it is a string and starts with a { (JSON)
      if (typeof data === 'string' && data.startsWith('{')) {
        data = JSON.parse(data);
      }

      // Trustarc consent messages
      if (data?.source === 'preference_manager' && data?.message === 'submit_preferences') {
        setAreFunctionalCookiesEnabled(isTrustarcCookieTypeAllowed({ cookiePreference: data?.data?.value, type: 'functional' }));
        setAreAdvertisingCookiesEnabled(isTrustarcCookieTypeAllowed({ cookiePreference: data?.data?.value, type: 'advertising' }));
        
        // Close TrustArc banner manually
        // TrustArc creates multiple elements with same ID, so we need to hide all of them
        setTimeout(() => {
          const banners = document.querySelectorAll('#truste-consent-track');
          banners.forEach(banner => {
            if (banner instanceof HTMLElement) {
              banner.style.display = 'none';
              banner.classList.remove('ta-show', 'ta-display-block');
              banner.classList.add('ta-hide', 'ta-display-hide');
            }
          });
        }, 100);
      }
    } catch (e) {
      console.error('PreferencesContextProvider: ', e);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleNewMessages, { passive: true });
    return () => {
      window.removeEventListener('message', handleNewMessages);
    };
  }, [handleNewMessages]);

  useEffect(() => {
    if (router.query && !!router.query.openSearch && isExpUsTenant) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsMapSearchOpen(true);
    }
  }, [isExpUsTenant, router.query]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    getUserLastSearchParams();
  }, [getUserLastSearchParams, user]);

  useEffect(() => {
    // Remove functional scripts
    if (!areFunctionalCookiesEnabled) {
      document.getElementById(ACTIVECAMPAIGN_SCRIPT)?.remove();
      document.getElementById(GTM_SCRIPT)?.remove();
      document.getElementById(NEWRELIC_SCRIPT)?.remove();
    }
  }, [areFunctionalCookiesEnabled, areAdvertisingCookiesEnabled]);

  const updateSchoolParams = useCallback((params: SchoolParams) => {
    updateSchoolParamsCookie(params);
    setSchoolParams(createSchoolParams(params));
  }, [setSchoolParams]);

  const contextValue = useMemo(() => ({
    listingParams,
    schoolParams,
    screen,
    recentSearches,
    detailedRecentSearches,
    lastSearchLocation,
    areFunctionalCookiesEnabled,
    isMapSearchOpen,
    addToRecentSearches,
    refreshCurrentLocationFilter,
    updateSchoolParams,
    setIsMapSearchOpen,
    isAiSearch,
    setIsAiSearch,
  }), [
    listingParams,
    schoolParams,
    screen,
    recentSearches,
    detailedRecentSearches,
    lastSearchLocation,
    areFunctionalCookiesEnabled,
    isMapSearchOpen,
    addToRecentSearches,
    refreshCurrentLocationFilter,
    updateSchoolParams,
    setIsMapSearchOpen,
    isAiSearch,
    setIsAiSearch,
  ]);

  return (
    <PreferencesContext.Provider value={contextValue}>
      {children}
    </PreferencesContext.Provider>
  );
}

